
//#region AUTO ORDER - ORDER ITEMS

/**
 * ### PATH_AUTO_ORDER_ORDER_BY_STORE_ID_AND_ORDER_ID
 * Constructs the Firestore path to access a specific auto-generated order document for a given store.
 *
 * @function PATH_AUTO_ORDER_ORDER_BY_STORE_ID_AND_ORDER_ID
 * @param {string} storeId - The unique identifier of the store.
 * @param {string} orderId - The unique identifier of the auto-generated order.
 * @returns {string} Firestore path to the auto order document.
 *
 * @directory
 * ```text
 * operational
 * └── stores_data
 *     └── ${storeId}
 *         └── data
 *             └── auto_ordering
 *                 └── auto_orders
 *                     └── orders
 *                         └── ${orderId}
 * ```
 *
 * @example
 * ```ts
 * const path = PATH_AUTO_ORDER_ORDER_BY_STORE_ID_AND_ORDER_ID('store-123', 'order-456');
 * // "/operational/stores_data/store-123/data/auto_ordering/auto_orders/orders/order-456"
 * ```
 */
export const PATH_AUTO_ORDER_ORDER_BY_STORE_ID_AND_ORDER_ID = (storeId: string, orderId: string): string => {
  return `/operational/stores_data/${storeId}/data/auto_ordering/auto_orders/orders/${orderId}`;
};

/**
 * ### PATH_AUTO_ORDER_ORDER_ITEMS_BY_STORE_ID_AND_ORDER_ID
 * Constructs the Firestore path to retrieve all `orderItems` for a specific store and auto order.
 *
 * @function PATH_AUTO_ORDER_ORDER_ITEMS_BY_STORE_ID_AND_ORDER_ID
 * @param {string} storeId - The unique identifier of the store.
 * @param {string} orderId - The unique identifier of the auto-generated order.
 * @returns {string} Firestore path to the order items collection.
 *
 * @directory
 * ```text
 * operational
 * └── stores_data
 *     └── ${storeId}
 *         └── data
 *             └── auto_ordering
 *                 └── auto_orders
 *                     └── orders
 *                         └── ${orderId}
 *                             └── orderItems
 * ```
 *
 * @example
 * ```ts
 * const path = PATH_AUTO_ORDER_ORDER_ITEMS_BY_STORE_ID_AND_ORDER_ID('store-123', 'order-456');
 * // "/operational/stores_data/store-123/data/auto_ordering/auto_orders/orders/order-456/orderItems"
 * ```
 */
export const PATH_AUTO_ORDER_ORDER_ITEMS_BY_STORE_ID_AND_ORDER_ID = (storeId: string, orderId: string): string => {
  return `${PATH_AUTO_ORDER_ORDER_BY_STORE_ID_AND_ORDER_ID(storeId, orderId)}/orderItems`;
};

/**
 * ### PATH_AUTO_ORDER_ORDER_ITEMS_BY_STORE_ID_AND_ORDER_ID_AT_ITEM_CODE
 * Constructs the Firestore path to a specific `orderItem` document identified by its `stockCode`
 * within a particular store's auto-generated order.
 *
 * @function PATH_AUTO_ORDER_ORDER_ITEMS_BY_STORE_ID_AND_ORDER_ID_AT_ITEM_CODE
 * @param {string} storeId - The unique identifier of the store.
 * @param {string} orderId - The unique identifier of the auto-generated order.
 * @param {string} stockCode - The unique stock code representing the individual order item.
 * @returns {string} Firestore document path to the specific `orderItem`.
 *
 * @directory
 * ```text
 * operational
 * └── stores_data
 *     └── ${storeId}
 *         └── data
 *             └── auto_ordering
 *                 └── auto_orders
 *                     └── orders
 *                         └── ${orderId}
 *                             └── orderItems
 *                                 └── ${stockCode}
 * ```
 *
 * @example
 * ```ts
 * const path = PATH_AUTO_ORDER_ORDER_ITEMS_BY_STORE_ID_AND_ORDER_ID_AT_ITEM_CODE('store-123', 'order-456', 'SKU-789');
 * // "/operational/stores_data/store-123/data/auto_ordering/auto_orders/orders/order-456/orderItems/SKU-789"
 * ```
 */
export const PATH_AUTO_ORDER_ORDER_ITEMS_BY_STORE_ID_AND_ORDER_ID_AT_ITEM_CODE = (
  storeId: string,
  orderId: string,
  stockCode: string,
): string => {
  return `${PATH_AUTO_ORDER_ORDER_ITEMS_BY_STORE_ID_AND_ORDER_ID(storeId, orderId)}/${stockCode}`;
};

//#endregion

//#region QUOTES
/**
 * ### PATH_OPERATIONAL_STORE_DATA_QUOTES_AREA_GET
 * Root under store `data/` for all quotes-related collections (room for future docs).
 */
export const PATH_OPERATIONAL_STORE_DATA_QUOTES_AREA_GET = (storeId: string): string => {
  return `operational/stores_data/${storeId}/data/quotes`;
};

/**
 * ### PATH_OPERATIONAL_STORE_DATA_QUOTES_GET
 * Collection of submitted quote documents.
 *
 * Full path: `operational/stores_data/{storeId}/data/quotes/quotes/quotes`
 */
export const PATH_OPERATIONAL_STORE_DATA_QUOTES_GET = (storeId: string): string => {
  return `${PATH_OPERATIONAL_STORE_DATA_QUOTES_AREA_GET(storeId)}/quotes/quotes`;
};

/**
 * ### PATH_OPERATIONAL_STORE_DATA_QUOTE_BY_ID
 * Constructs the Firestore path to a specific quote document.
 */
export const PATH_OPERATIONAL_STORE_DATA_QUOTE_BY_ID = (storeId: string, quoteId: string): string => {
  return `${PATH_OPERATIONAL_STORE_DATA_QUOTES_GET(storeId)}/${quoteId}`;
};

/**
 * ### PATH_OPERATIONAL_STORE_DATA_QUOTE_ITEMS_GET
 * Constructs the Firestore path to the quote_items subcollection for a quote.
 */
export const PATH_OPERATIONAL_STORE_DATA_QUOTE_ITEMS_GET = (storeId: string, quoteId: string): string => {
  return `${PATH_OPERATIONAL_STORE_DATA_QUOTE_BY_ID(storeId, quoteId)}/quote_items`;
};

/**
 * ### PATH_OPERATIONAL_STORE_DATA_QUOTE_ITEM_BY_CODE
 * Constructs the Firestore path to a single quote_items document.
 */
export const PATH_OPERATIONAL_STORE_DATA_QUOTE_ITEM_BY_CODE = (
  storeId: string,
  quoteId: string,
  stockCode: string,
): string => {
  return `${PATH_OPERATIONAL_STORE_DATA_QUOTE_ITEMS_GET(storeId, quoteId)}/${stockCode}`;
};
//#endregion
